import { Loader2, TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Timeline, type ViewState } from "~/components/timeline";
import { useChunkedTimelineLoader } from "~/features/entities/hooks/useChunkedTimelineLoader";
import type { EntityListFilters } from "~/features/entities/types";
import { buildEntitiesWhere } from "~/features/entities/utils/transformers";
import { useTenant } from "~/features/tenant/context";
import { parseGdtfRange } from "~/utils/gdtfRange";

/** Max years the timeline can render at minimum zoom (~800px / 0.1 scale). */
const TIMELINE_MAX_YEAR_RANGE = 8000;

interface EntitiesTimelineViewProps {
  filters: EntityListFilters;
  onSelect: (id: string) => void;
  initialViewState?: ViewState;
  onViewStateChange?: (centerYear: number, scale: number) => void;
}

export function EntitiesTimelineView({
  filters,
  onSelect,
  initialViewState,
  onViewStateChange,
}: EntitiesTimelineViewProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");

  const gdtfParsed = useMemo(
    () => parseGdtfRange(filters.gdtfFrom, filters.gdtfTo),
    [filters.gdtfFrom, filters.gdtfTo],
  );

  const gdtfRangeExceedsCapacity = useMemo(() => {
    if (!gdtfParsed.hasFilter || gdtfParsed.inverted) return false;
    if (gdtfParsed.fromYear == null || gdtfParsed.toYear == null) return false;
    return Math.abs(gdtfParsed.toYear - gdtfParsed.fromYear) > TIMELINE_MAX_YEAR_RANGE;
  }, [gdtfParsed]);

  // Restrict to entity records; the chunked loader adds per-chunk dateJdn bounds on top.
  const baseWhere = useMemo(() => ({ ...buildEntitiesWhere(filters), entityRecord: {} }), [filters]);
  const filterHash = useMemo(() => JSON.stringify(baseWhere), [baseWhere]);

  const { entities, loading, onVisibleYearRangeChange } = useChunkedTimelineLoader({
    tenantId,
    baseWhere,
    filterHash,
    orderBy: { date: "ASC" },
    gdtfFrom: filters.gdtfFrom,
    gdtfTo: filters.gdtfTo,
  });

  // When a GDTF date filter is active, center the timeline on the filtered range.
  const gdtfViewState = useMemo<ViewState | undefined>(() => {
    if (!gdtfParsed.hasFilter || gdtfParsed.inverted) return undefined;
    const { fromYear, toYear } = gdtfParsed;
    if (fromYear == null && toYear == null) return undefined;
    const lo = fromYear ?? (toYear as number) - 500;
    const hi = toYear ?? (fromYear as number) + 500;
    const center = (lo + hi) / 2;
    const rangeYears = Math.max(1, hi - lo);
    // Fit the range into ~80% of an assumed 800px canvas; the Timeline's ResizeObserver corrects later.
    const scale = Math.max(0.1, 640 / rangeYears);
    return { offsetX: 400 - center * scale, offsetY: 0, scale };
  }, [gdtfParsed]);

  // With GDTF active, remount on filter change so the timeline snaps to the GDTF range. Otherwise keep
  // a static key so the user's pan/zoom position is preserved across re-renders.
  const timelineKey = gdtfViewState ? filterHash : "default";

  return (
    <div className="space-y-2">
      {gdtfRangeExceedsCapacity && (
        <p className="flex items-center gap-2 rounded-[var(--radius-sm)] bg-warning-bg px-3 py-2 text-12-regular text-fg-1">
          <TriangleAlert className="h-4 w-4 shrink-0 text-warning" aria-hidden />
          {t("ui.gdtfTimelineCapacityWarning", { maxYears: TIMELINE_MAX_YEAR_RANGE })}
        </p>
      )}
      <div className="relative rounded-[var(--radius-md)] border border-border-default bg-white">
        {loading && (
          <Loader2 className="absolute right-2 top-2 z-10 h-4 w-4 animate-spin text-fg-3" aria-label="loading" />
        )}
        <Timeline
          key={timelineKey}
          entities={entities}
          height={600}
          storageKey="v2-entities-list"
          onEntityClick={(entity) => onSelect(entity.id)}
          onVisibleYearRangeChange={onVisibleYearRangeChange}
          initialViewState={gdtfViewState ?? initialViewState}
          onViewStateChange={onViewStateChange}
        />
      </div>
    </div>
  );
}
